import {
	notApplicableClickedElement,
	type ClickedElementAttributes,
	type MessageInstanceClickedParams,
} from '@atlassian/post-office-context/message-lifecycle';

import type {
	AcknowledgedInput,
	ClosedInput,
	CtaActionedInput,
	DeclinedInput,
	SnoozedInput,
	StepChangedInput,
} from '../types';

/**
 * Internal discriminated union of the six supported semantic interactions.
 *
 * Message Makers never construct this — `kind` is supplied by the named handler they
 * call, which is what makes the interaction kind unambiguous at the call site.
 */
export type SemanticInteraction =
	| ({ kind: 'ctaActioned' } & CtaActionedInput)
	| ({ kind: 'acknowledged' } & AcknowledgedInput)
	| ({ kind: 'declined' } & DeclinedInput)
	| ({ kind: 'snoozed' } & SnoozedInput)
	| ({ kind: 'closed' } & ClosedInput)
	| ({ kind: 'stepChanged' } & StepChangedInput);

/**
 * The legacy `clickedElement` shape for kinds that downstream consumers still read as a
 * primary click. Presentation is not intent — this is emitted for backward compatibility
 * only and is removed once consumers read `action.actionType` alone.
 */
const primaryButton = (interactionIdentifier: string): ClickedElementAttributes => ({
	clickIdentifier: interactionIdentifier,
	clickedElement: 'button',
	buttonType: 'primary',
});

/**
 * The legacy `clickedElement` shape for kinds whose meaning does not depend on a rendered
 * element. `closed` in particular covers Escape, click-outside and navigating away.
 *
 * Delegates to `notApplicableClickedElement` rather than building the literal directly —
 * that factory is the only way to produce this branded, PO-owned value.
 */
const notApplicable = (interactionIdentifier: string): ClickedElementAttributes =>
	notApplicableClickedElement(interactionIdentifier);

type ClickedEventCore = Pick<MessageInstanceClickedParams, 'clickedElement' | 'action'>;

/**
 * Maps a semantic interaction to the `clickedElement` and `action` pair defined by the
 * canonical compatibility mapping.
 *
 * The switch is deliberately exhaustive with no `default`: adding a kind to
 * `SemanticInteraction` without mapping it here is a compile error.
 *
 * @see https://hello.atlassian.net/wiki/spaces/POSTOFFICE/pages/7480820497/LYT+IPMs+Semantic+Interaction+Contract+Proposal+Rationale+and+Mapping#1.7-End-to-end-mapping
 */
const toClickedEventCore = (interaction: SemanticInteraction): ClickedEventCore => {
	const identifier = interaction.interactionIdentifier;

	switch (interaction.kind) {
		case 'ctaActioned':
			return {
				clickedElement: primaryButton(identifier),
				action: {
					actionType: 'open',
					...(interaction.destinationUrl !== undefined && {
						destinationUrl: interaction.destinationUrl,
					}),
					...(interaction.trackingLinkId !== undefined && {
						trackingLinkId: interaction.trackingLinkId,
					}),
				},
			};

		case 'acknowledged':
			return {
				clickedElement: primaryButton(identifier),
				action: { actionType: 'acknowledge' },
			};

		case 'declined':
			return {
				clickedElement: notApplicable(identifier),
				action: { actionType: 'dismiss' },
			};

		case 'snoozed':
			return {
				clickedElement: notApplicable(identifier),
				action: { actionType: 'snooze', snoozeDuration: interaction.durationDays },
			};

		case 'closed':
			// Compatibility mode: `closed` maps to `dismiss` because there is no native
			// `close` actionType yet. Native support is tracked separately, and the split
			// between `declined` and `closed` is preserved in `interactionIdentifier`.
			return {
				clickedElement: notApplicable(identifier),
				action: { actionType: 'dismiss' },
			};

		case 'stepChanged':
			return {
				clickedElement: notApplicable(identifier),
				action: {
					actionType: interaction.direction,
					currentStep: interaction.toStep,
					previousStep: interaction.fromStep,
					...(interaction.stepName !== undefined && { stepName: interaction.stepName }),
				},
			};
	}
};

/**
 * Maps a semantic interaction to the backward-compatible `MessageInstanceClickedEvent`
 * representation, excluding the context that Post Office binds separately.
 *
 * `interactionIdentifier` is emitted at the top level and mirrored into
 * `clickedElement.clickIdentifier`; runtime validation requires the two to match.
 */
export const mapInteractionToClickedEvent = (
	interaction: SemanticInteraction,
): MessageInstanceClickedParams => ({
	messageEventType: 'clicked',
	interactionIdentifier: interaction.interactionIdentifier,
	...toClickedEventCore(interaction),
	...(interaction.additionalMetadata !== undefined && {
		additionalMetadataFromFrontend: interaction.additionalMetadata,
	}),
});
